import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  COMMENT_THREAD_STYLE,
  groupThreads,
  type BlockComment,
  type CommentThread,
} from '@hub/contracts'
import { useIsMobile } from '@/hooks/use-mobile'
import { HUB_BUBBLE_ASK_EVENT, type HubBubbleAskDetail } from '@/components/chat/hub-bubble'
import type { BlockEditorHandle, CommentDraft } from '@/components/documents/block-editor'
import type { DocumentClient } from './document-client'
import { stripCommentMarkdown } from '@hub/contracts'
import { readCommentPrefs, writeCommentPrefs } from './comment-prefs'

/**
 * Comment UI state for one open document (PLAN margin comments §4/§8):
 * threads, the active/hovered thread, per-thread expansion, "Visa lösta",
 * the new-thread composer and the layout mode. The DOM measuring lives in
 * `comment-margin.tsx`; this hook is container-agnostic.
 */

export type CommentLayoutMode = 'margin' | 'narrow' | 'phone'

/** Row width from which the margin column fits next to the text (§17.12). */
export const MARGIN_MODE_MIN_ROW_WIDTH = 1100
/** A comment not echoed back by the store within this is treated as refused (draft kept). */
const STORE_CONFIRM_TIMEOUT_MS = 10_000

export interface DocumentCommentsState {
  threads: CommentThread[]
  openThreads: CommentThread[]
  resolvedThreads: CommentThread[]
  activeId: string | null
  hoverId: string | null
  expanded: ReadonlySet<string>
  showResolved: boolean
  /** "Dölj alla kommentarer": no column, markers or highlights. */
  hidden: boolean
  composer: CommentDraft | null
  select: (id: string | null) => void
  hover: (id: string | null) => void
  toggleExpanded: (id: string) => void
  setShowResolved: (on: boolean) => void
  setHidden: (on: boolean) => void
  startComposer: (draft: CommentDraft) => void
  cancelComposer: () => void
  /** Resolves true once the store has broadcast the new comment; false on refusal / timeout. */
  submitComposer: (body: string) => Promise<boolean>
  reply: (threadId: string, body: string) => Promise<boolean>
  resolve: (threadId: string, resolved: boolean) => void
  remove: (thread: CommentThread, commentId: string) => void
  sendToHub: (thread: CommentThread) => void
  /** Whether a thread's block still exists (orphan otherwise). */
  isOrphan: (thread: CommentThread) => boolean
}

export function useDocumentComments(
  client: DocumentClient | null,
  comments: BlockComment[],
  editorRef: RefObject<BlockEditorHandle | null>,
  userId: string | undefined
): DocumentCommentsState {
  const { t } = useTranslation('editor')
  const threads = useMemo(() => groupThreads(comments), [comments])
  const openThreads = useMemo(() => threads.filter((t) => !t.resolvedAt), [threads])
  const resolvedThreads = useMemo(() => threads.filter((t) => !!t.resolvedAt), [threads])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [hoverId, setHoverId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [prefs, setPrefs] = useState(() => readCommentPrefs(userId))
  // `me` can resolve after mount: re-read so the user's own choice (not the anonymous key) applies.
  useEffect(() => {
    setPrefs(readCommentPrefs(userId))
  }, [userId])
  const showResolved = prefs.showResolved
  const hidden = prefs.hidden
  const setShowResolved = useCallback(
    (on: boolean) =>
      setPrefs((prev) => {
        const next = { ...prev, showResolved: on }
        writeCommentPrefs(userId, next)
        return next
      }),
    [userId]
  )
  const setHidden = useCallback(
    (on: boolean) =>
      setPrefs((prev) => {
        const next = { ...prev, hidden: on }
        writeCommentPrefs(userId, next)
        return next
      }),
    [userId]
  )
  const [composer, setComposer] = useState<CommentDraft | null>(null)
  const composerRef = useRef(composer)
  composerRef.current = composer

  // Server refusals ("root has replies", a rejected message, …) → toast.
  useEffect(() => {
    if (!client) return
    return client.on('error', (message) => {
      if (message === 'comment:has-replies') toast.error(t('comments.errorHasReplies'))
      else if (message === 'comment:missing') toast.error(t('comments.errorMissing'))
      else toast.error(t('comments.errorGeneric'))
    })
  }, [client, t])

  /**
   * The store has no per-comment ack: a comment is confirmed when the next
   * `comments` broadcast contains it, refused when an `error` arrives first.
   * The composer keeps its draft until this resolves true.
   */
  const awaitStored = useCallback(
    (match: (c: BlockComment) => boolean): Promise<boolean> => {
      if (!client) return Promise.resolve(false)
      return new Promise((resolve) => {
        let done = false
        const finish = (ok: boolean) => {
          if (done) return
          done = true
          offComments()
          offError()
          clearTimeout(timer)
          resolve(ok)
        }
        const offComments = client.on('comments', (list) => {
          if (list.some(match)) finish(true)
        })
        const offError = client.on('error', () => finish(false))
        const timer = setTimeout(() => {
          toast.error(t('comments.errorGeneric'))
          finish(false)
        }, STORE_CONFIRM_TIMEOUT_MS)
      })
    },
    [client, t]
  )

  // A thread that disappeared (deleted elsewhere) cannot stay active; a card
  // that unmounted under the pointer (resolved) leaves no stale hover either.
  useEffect(() => {
    if (activeId && activeId !== 'new' && !threads.some((th) => th.id === activeId))
      setActiveId(null)
    if (hoverId && !openThreads.some((th) => th.id === hoverId) && !showResolved) setHoverId(null)
  }, [threads, openThreads, activeId, hoverId, showResolved])

  // Click elsewhere / Escape → no thread active (the new-thread composer keeps its card).
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as Element | null
      if (!el || typeof el.closest !== 'function') return
      if (
        el.closest('[data-thread-id]') ||
        el.closest(`[data-style-type="${COMMENT_THREAD_STYLE}"]`) ||
        el.closest('[data-radix-popper-content-wrapper]') ||
        el.closest('[data-slot="drawer-content"]') ||
        el.closest('[data-slot="sheet-content"]') ||
        el.closest('[data-mention-menu]') ||
        el.closest('[data-marker]')
      )
        return
      setActiveId((current) => (current === 'new' ? current : null))
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const el = e.target as Element | null
      if (el?.closest?.('[data-composer]')) return
      setActiveId((current) => (current === 'new' ? current : null))
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const select = useCallback((id: string | null) => setActiveId(id), [])
  const hover = useCallback((id: string | null) => setHoverId(id), [])
  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const startComposer = useCallback(
    (draft: CommentDraft) => {
      const previous = composerRef.current
      if (previous?.threadId && previous.threadId !== draft.threadId)
        editorRef.current?.removeCommentHighlight(previous.threadId)
      setComposer(draft)
      setActiveId('new')
      // Writing a comment while they are hidden shows them again.
      setHidden(false)
    },
    [editorRef, setHidden]
  )

  const cancelComposer = useCallback(() => {
    const draft = composerRef.current
    if (draft?.threadId) editorRef.current?.removeCommentHighlight(draft.threadId)
    setComposer(null)
    setActiveId(null)
  }, [editorRef])

  const submitComposer = useCallback(
    async (body: string) => {
      const draft = composerRef.current
      if (!draft || !client) return false
      const stored = awaitStored(
        (c) =>
          c.parentId === null &&
          c.blockId === draft.blockId &&
          c.body === body &&
          (draft.threadId ? c.id === draft.threadId : c.authorId === userId)
      )
      client.addComment({
        blockId: draft.blockId,
        body,
        bodyFormat: 'md',
        id: draft.threadId ?? undefined,
        quotedText: draft.quotedText,
      })
      const ok = await stored
      if (!ok) return false
      // Only now: the draft card goes away and the new thread's card takes over.
      setComposer(null)
      setActiveId(draft.threadId ?? null)
      return true
    },
    [client, awaitStored, userId]
  )

  const reply = useCallback(
    async (threadId: string, body: string) => {
      if (!client) return false
      const stored = awaitStored(
        (c) => c.parentId === threadId && c.body === body && c.authorId === userId
      )
      client.replyComment(threadId, body, 'md')
      return stored
    },
    [client, awaitStored, userId]
  )
  const resolve = useCallback(
    (threadId: string, resolved: boolean) => {
      client?.resolveThread(threadId, resolved)
      if (resolved) setActiveId((current) => (current === threadId ? null : current))
    },
    [client]
  )
  const remove = useCallback(
    (thread: CommentThread, commentId: string) => {
      if (!client) return
      client.deleteComment(commentId)
      // Deleting the root removes the thread: its highlight goes with it.
      if (commentId === thread.id && thread.replies.length === 0)
        editorRef.current?.removeCommentHighlight(thread.id)
    },
    [client, editorRef]
  )

  const isOrphan = useCallback(
    (thread: CommentThread) => !client || !findBlockId(client, thread.blockId),
    [client]
  )

  const sendToHub = useCallback((thread: CommentThread) => {
    const lines: string[] = []
    if (thread.quotedText) lines.push(`> ${thread.quotedText.replace(/\n/g, '\n> ')}`, '')
    const all = [thread.root, ...thread.replies]
    for (const c of all)
      lines.push(`**${c.authorName}:** ${stripCommentMarkdown(c.body, c.bodyFormat)}`)
    window.dispatchEvent(
      new CustomEvent<HubBubbleAskDetail>(HUB_BUBBLE_ASK_EVENT, {
        detail: { blockId: thread.blockId, prompt: lines.join('\n') },
      })
    )
  }, [])

  // Memoised: the route puts this object in `useMemo` deps (top-bar actions).
  return useMemo(
    () => ({
      threads,
      openThreads,
      resolvedThreads,
      activeId,
      hoverId,
      expanded,
      showResolved,
      hidden,
      composer,
      select,
      hover,
      toggleExpanded,
      setShowResolved,
      setHidden,
      startComposer,
      cancelComposer,
      submitComposer,
      reply,
      resolve,
      remove,
      sendToHub,
      isOrphan,
    }),
    [
      threads,
      openThreads,
      resolvedThreads,
      activeId,
      hoverId,
      expanded,
      showResolved,
      hidden,
      composer,
      select,
      hover,
      toggleExpanded,
      setShowResolved,
      setHidden,
      startComposer,
      cancelComposer,
      submitComposer,
      reply,
      resolve,
      remove,
      sendToHub,
      isOrphan,
    ]
  )
}

function findBlockId(client: DocumentClient, blockId: string): boolean {
  const walk = (blocks: typeof client.blocks): boolean =>
    blocks.some((b) => b.id === blockId || walk(b.children))
  return walk(client.blocks)
}

/**
 * Layout mode from the *row* width (the sidebar changes it, the viewport
 * does not) and the phone media query. Never remounts the editor: the row
 * keeps the editor at the same tree position in every mode.
 */
export function useCommentLayoutMode(): {
  mode: CommentLayoutMode
  /** Callback ref for the row element. */
  rowRef: (el: HTMLElement | null) => void
  rowEl: HTMLElement | null
} {
  const isMobile = useIsMobile()
  const [row, setRow] = useState<HTMLElement | null>(null)
  const [wide, setWide] = useState(true)
  useEffect(() => {
    if (!row) return
    const update = () => setWide(row.getBoundingClientRect().width >= MARGIN_MODE_MIN_ROW_WIDTH)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(row)
    return () => ro.disconnect()
  }, [row])
  return { mode: isMobile ? 'phone' : wide ? 'margin' : 'narrow', rowRef: setRow, rowEl: row }
}
